import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts'

interface Metrics {
  date: string
  impressions: number
  link_clicks: number
  spend: number
  conversions: number
  cpm: number
  cpc: number
  ctr: number
  roas: number
}

export default function DashboardPage() {
  const { campaignId } = useParams<{ campaignId: string }>()
  const [dateRange, setDateRange] = useState('last_7d')

  // Mock data - in real app this would come from API
  const campaign = {
    id: campaignId,
    name: 'Campaign 12/19/2024',
    status: 'ACTIVE',
    budget: 100000, // cents
    spent: 15430, // cents
    start_date: '2024-12-19',
    end_date: '2024-12-26'
  }

  const mockMetrics: Metrics[] = [
    { date: '2024-12-19', impressions: 1500, link_clicks: 45, spend: 15.43, conversions: 3, cpm: 10.29, cpc: 0.34, ctr: 3.0, roas: 2.8 },
    { date: '2024-12-20', impressions: 2200, link_clicks: 67, spend: 22.80, conversions: 5, cpm: 10.36, cpc: 0.34, ctr: 3.05, roas: 3.2 },
    { date: '2024-12-21', impressions: 1800, link_clicks: 52, spend: 18.90, conversions: 4, cpm: 10.50, cpc: 0.36, ctr: 2.89, roas: 2.9 },
    { date: '2024-12-22', impressions: 2500, link_clicks: 78, spend: 26.70, conversions: 6, cpm: 10.68, cpc: 0.34, ctr: 3.12, roas: 3.5 },
    { date: '2024-12-23', impressions: 1950, link_clicks: 61, spend: 20.85, conversions: 4, cpm: 10.69, cpc: 0.34, ctr: 3.13, roas: 3.1 }
  ]

  const totalMetrics = mockMetrics.reduce((acc, metric) => ({
    impressions: acc.impressions + metric.impressions,
    link_clicks: acc.link_clicks + metric.link_clicks,
    spend: acc.spend + metric.spend,
    conversions: acc.conversions + metric.conversions
  }), { impressions: 0, link_clicks: 0, spend: 0, conversions: 0 })

  const avgCTR = (totalMetrics.link_clicks / totalMetrics.impressions * 100).toFixed(2)
  const avgCPC = (totalMetrics.spend / totalMetrics.link_clicks).toFixed(2)
  const avgROAS = ((totalMetrics.conversions * 50) / totalMetrics.spend).toFixed(2) // Assuming $50 per conversion

  return (
    <div style={{
      width: '100%',
      padding: 'var(--spacing-8) 0',
      fontFamily: 'var(--font-family-appleSystem)',
    }}>
      <div style={{
        background: 'var(--color-card)',
        borderRadius: '24px',
        boxShadow: 'var(--shadow-appleModal)',
        border: '1px solid var(--color-border)',
        padding: 'var(--spacing-8)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-6)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--spacing-6)',
        }}>
          <div>
            <h2 style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: 700,
              color: 'var(--color-label)',
              marginBottom: 'var(--spacing-1)',
              letterSpacing: 'var(--letter-spacing-tight)',
            }}>
              {campaign.name}
            </h2>
            <p style={{
              color: 'var(--color-secondaryLabel)',
              fontSize: 'var(--font-size-base)',
              fontWeight: 500,
            }}>
              Campaign Performance Dashboard
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid var(--color-border)',
                background: 'var(--color-systemBackground)',
                color: 'var(--color-label)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'inherit',
                outline: 'none',
              }}
            >
              <option value="last_7d">Last 7 days</option>
              <option value="last_14d">Last 14 days</option>
              <option value="last_30d">Last 30 days</option>
            </select>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              background: campaign.status === 'ACTIVE' ? 'var(--color-systemGreen)' : 'var(--color-tertiaryLabel)',
              color: 'var(--color-systemBackground)',
            }}>
              {campaign.status}
            </div>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 'var(--spacing-4)',
          marginBottom: 'var(--spacing-6)',
        }}>
          <div style={{
            background: 'var(--color-systemBackground)',
            borderRadius: '16px',
            padding: 'var(--spacing-5)',
            border: '1px solid var(--color-border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                background: 'var(--color-systemBlue)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg style={{ width: 20, height: 20, color: 'var(--color-systemBackground)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div>
                <p style={{
                  color: 'var(--color-secondaryLabel)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                  marginBottom: 2,
                }}>
                  Impressions
                </p>
                <p style={{
                  color: 'var(--color-label)',
                  fontSize: 'var(--font-size-xl)',
                  fontWeight: 600,
                }}>
                  {totalMetrics.impressions.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div style={{
            background: 'var(--color-systemBackground)',
            borderRadius: '16px',
            padding: 'var(--spacing-5)',
            border: '1px solid var(--color-border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                background: 'var(--color-systemGreen)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg style={{ width: 20, height: 20, color: 'var(--color-systemBackground)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
              </div>
              <div>
                <p style={{
                  color: 'var(--color-secondaryLabel)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                  marginBottom: 2,
                }}>
                  Link Clicks
                </p>
                <p style={{
                  color: 'var(--color-label)',
                  fontSize: 'var(--font-size-xl)',
                  fontWeight: 600,
                }}>
                  {totalMetrics.link_clicks.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div style={{
            background: 'var(--color-systemBackground)',
            borderRadius: '16px',
            padding: 'var(--spacing-5)',
            border: '1px solid var(--color-border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                background: 'var(--color-systemOrange)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg style={{ width: 20, height: 20, color: 'var(--color-systemBackground)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div>
                <p style={{
                  color: 'var(--color-secondaryLabel)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                  marginBottom: 2,
                }}>
                  Spend
                </p>
                <p style={{
                  color: 'var(--color-label)',
                  fontSize: 'var(--font-size-xl)',
                  fontWeight: 600,
                }}>
                  ${totalMetrics.spend.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div style={{
            background: 'var(--color-systemBackground)',
            borderRadius: '16px',
            padding: 'var(--spacing-5)',
            border: '1px solid var(--color-border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                background: 'var(--color-systemPurple)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg style={{ width: 20, height: 20, color: 'var(--color-systemBackground)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p style={{
                  color: 'var(--color-secondaryLabel)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                  marginBottom: 2,
                }}>
                  Results
                </p>
                <p style={{
                  color: 'var(--color-label)',
                  fontSize: 'var(--font-size-xl)',
                  fontWeight: 600,
                }}>
                  {totalMetrics.conversions}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: 'var(--spacing-6)',
          marginBottom: 'var(--spacing-6)',
        }}>
          {/* Performance Over Time */}
          <div style={{
            background: 'var(--color-systemBackground)',
            borderRadius: '16px',
            padding: 'var(--spacing-6)',
            border: '1px solid var(--color-border)',
          }}>
            <h3 style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 600,
              color: 'var(--color-label)',
              marginBottom: 'var(--spacing-4)',
            }}>
              Performance Over Time
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={mockMetrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="impressions" stroke="var(--color-primary)" strokeWidth={2} />
                <Line type="monotone" dataKey="link_clicks" stroke="var(--color-success)" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Daily Spend */}
          <div style={{
            background: 'var(--color-systemBackground)',
            borderRadius: '16px',
            padding: 'var(--spacing-6)',
            border: '1px solid var(--color-border)',
          }}>
            <h3 style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 600,
              color: 'var(--color-label)',
              marginBottom: 'var(--spacing-4)',
            }}>
              Daily Spend
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={mockMetrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="spend" fill="var(--color-warning)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Performance Metrics */}
        <div style={{
          background: 'var(--color-systemBackground)',
          borderRadius: '16px',
          border: '1px solid var(--color-border)',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: 'var(--spacing-6)',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-tertiarySystemBackground)',
          }}>
            <h3 style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 600,
              color: 'var(--color-label)',
            }}>
              Key Performance Indicators
            </h3>
          </div>
          <div style={{
            padding: 'var(--spacing-6)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--spacing-6)',
          }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{
                color: 'var(--color-secondaryLabel)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                marginBottom: 'var(--spacing-2)',
              }}>
                Click-Through Rate
              </p>
              <p style={{
                color: 'var(--color-label)',
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 700,
              }}>
                {avgCTR}%
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{
                color: 'var(--color-secondaryLabel)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                marginBottom: 'var(--spacing-2)',
              }}>
                Cost Per Click
              </p>
              <p style={{
                color: 'var(--color-label)',
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 700,
              }}>
                ${avgCPC}
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{
                color: 'var(--color-secondaryLabel)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                marginBottom: 'var(--spacing-2)',
              }}>
                Return on Ad Spend
              </p>
              <p style={{
                color: 'var(--color-label)',
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 700,
              }}>
                {avgROAS}x
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 